import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, Banknote, FileText, Receipt, TrendingUp } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore, useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { PageHeader } from '@/app/DashboardLayout';
import {
  Button, Card, CardContent, EmptyState, ErrorState, Money,
  Table, TBody, TD, TH, THead, TR,
} from '@/features/common/ui';
import { formatDate } from '@/lib/dates';
import { MoneyInAndOut } from './MoneyInAndOut';
import { formatHuman, parse } from '@fint/money';
import { CfoDashboard } from '@/features/cfo/CfoDashboard';
import { DashboardModeToggle, useDashboardMode } from '@/features/cfo/DashboardMode';

interface DashboardSummary {
  currency: string;
  /** `later`: still owed, moved to a later date by the person (0039) - not in `overdue`. */
  receivables: { total: string; overdue: string; count: number; overdueCount: number; later?: string; laterCount?: number };
  expenses: { thisMonth: string; lastMonth: string };
  cash: {
    total: string;
    accounts: number;
    alsoHeld?: Array<{ currency: string; total: string; accounts: number }>;
    /** FDs, funds, chits: named beside cash, never in it. */
    investments?: { total: string; accounts: number };
  };
  recentInvoices: Array<{ id: string; number: string; customerName: string; total: string; status: string; date: string }>;
}

/**
 * The home screen: what you are owed, what you have spent, what you hold.
 *
 * Those three numbers in that order, because they are the questions a small
 * business owner opens an accounting app to answer - not a chart.
 */
export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const org = useActiveOrg();
  const perms = usePermissions();
  // CFO mode reads the whole book, so it is offered only to someone who may
  // see all of its reports; for anyone else the page is exactly as it was.
  // Platform staff only until the CFO's release (docs/roadmap.md).
  const offersCfo = perms.seesAll('report') && !!user?.cfo;
  const [chosenMode, setMode] = useDashboardMode();
  const mode = offersCfo ? chosenMode : 'standard';

  const query = useQuery({
    // orgId is ALWAYS the second element of a query key. Without it, switching
    // org shows the previous tenant's cached numbers under the new name.
    queryKey: ['dashboard', org?.id],
    queryFn: () => api.get<DashboardSummary>('/dashboard'),
    enabled: !!org?.id,
  });

  const currency = query.data?.currency ?? 'INR';

  return (
    <>
      <PageHeader
        title={greeting(user?.name)}
        subtitle={org ? `Here is where ${org.name} stands today.` : undefined}
        actions={
          offersCfo || perms.can('invoice', 'create') ? (
            <div className="flex items-center gap-2">
              {offersCfo ? <DashboardModeToggle value={mode} onChange={setMode} /> : null}
              {perms.can('invoice', 'create') ? (
                <Button asChild size="sm">
                  <Link to="/invoices/new">New invoice</Link>
                </Button>
              ) : null}
            </div>
          ) : null
        }
      />

      <div className="space-y-4 px-5 pb-8">
        {mode === 'cfo' ? (
          <CfoDashboard />
        ) : query.isError ? (
          <Card>
            <ErrorState error={query.error} onRetry={() => query.refetch()} />
          </Card>
        ) : query.isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="skeleton h-24 rounded-lg" />
            ))}
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile
                icon={FileText}
                label="Outstanding"
                value={<Money value={query.data?.receivables.total} currency={currency} />}
                caption={`${query.data?.receivables.count ?? 0} unpaid invoice${query.data?.receivables.count === 1 ? '' : 's'}`}
                href="/invoices?status=unpaid"
              />
              <StatTile
                icon={AlertTriangle}
                label="Overdue"
                value={<Money value={query.data?.receivables.overdue} currency={currency} />}
                caption={
                  `${query.data?.receivables.overdueCount ?? 0} past due` +
                  (query.data?.receivables.laterCount
                    ? ` · ${query.data.receivables.laterCount} moved to later`
                    : '')
                }
                tone={Number(query.data?.receivables.overdue ?? 0) > 0 ? 'danger' : 'normal'}
                href="/invoices?status=overdue"
              />
              <StatTile
                icon={Receipt}
                label="Spent this month"
                value={<Money value={query.data?.expenses.thisMonth} currency={currency} />}
                caption="Across all categories"
                href="/expenses"
              />
              {perms.can('banking', 'view') ? (
                <StatTile
                  icon={Banknote}
                  label="Cash on hand"
                  value={<Money value={query.data?.cash.total} currency={currency} />}
                  // Accounts in other currencies are named beside the figure,
                  // never added to it: £2,140 is not ₹2,140.
                  caption={[
                    `${query.data?.cash.accounts ?? 0} account${query.data?.cash.accounts === 1 ? '' : 's'}`,
                    ...(query.data?.cash.alsoHeld ?? []).map((h) => `also ${h.currency} ${h.total}`),
                    // An FD is not cash on hand, so it is named, not added.
                    ...(query.data?.cash.investments?.accounts
                      ? [`${formatHuman(parse(query.data.cash.investments.total, currency))} invested, not counted`]
                      : []),
                  ].join(' · ')}
                  href="/banking"
                />
              ) : null}
            </div>

            <Card>
              <div className="flex items-center justify-between border-b border-border px-5 py-3">
                <h2 className="flex items-center gap-2 text-sm font-semibold">
                  <TrendingUp className="size-4 text-muted-foreground" aria-hidden />
                  Recent invoices
                </h2>
                <Link to="/invoices" className="-my-2 flex min-h-9 items-center gap-1 text-sm text-primary hover:underline touch:min-h-11">
                  View all <ArrowRight className="size-3.5" aria-hidden />
                </Link>
              </div>
              {/*
                This branch used to render `<SkeletonTable rows={0} />` - a
                placeholder that was never finished, and one no test could
                catch: the empty branch below is correct, and every UI spec
                creates either no invoices or a handful in an org whose
                dashboard it never opens. So the card has been a blank white box
                for every user who has ever had an invoice, and green the whole
                time. Found by opening the app against two thousand of them.
              */}
              {query.data?.recentInvoices.length ? (
                <Table>
                  <THead>
                    <TR>
                      <TH>Invoice</TH>
                      <TH>Customer</TH>
                      <TH>Date</TH>
                      <TH numeric>Total</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {query.data.recentInvoices.map((invoice) => (
                      <TR key={invoice.id}>
                        <TD>
                          <Link
                            to={`/invoices/${invoice.id}`}
                            className="font-medium text-primary hover:underline"
                            data-tap-exempt="text link in a table cell; the hit area is drawn around it"
                          >
                            {invoice.number}
                          </Link>
                        </TD>
                        <TD className="max-w-[18rem] truncate">{invoice.customerName}</TD>
                        <TD className="text-muted-foreground">{formatDate(invoice.date)}</TD>
                        <TD numeric>
                          <Money value={invoice.total} currency={currency} />
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              ) : (
                <EmptyState
                  icon={FileText}
                  title="No invoices yet"
                  description="Raise your first invoice and it will show up here."
                  action={
                    perms.can('invoice', 'create') ? (
                      <Button asChild size="sm">
                        <Link to="/invoices/new">New invoice</Link>
                      </Button>
                    ) : null
                  }
                />
              )}
            </Card>

            {/*
              Added BELOW the existing tiles rather than replacing them. Those
              four answer "am I owed money", which this section does not and
              which losing would be a regression; this one answers "how did the
              month go". Two different questions, so the section carries its own
              heading saying which one it is.
            */}
            <MoneyInAndOut />
          </>
        )}
      </div>
    </>
  );
}

function StatTile({
  icon: Icon,
  label,
  value,
  caption,
  href,
  tone = 'normal',
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
  caption?: string;
  href?: string;
  tone?: 'normal' | 'danger';
}) {
  const body = (
    <CardContent className="pt-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className="size-4" aria-hidden />
        {label}
      </div>
      <p className={`mt-2 text-2xl font-semibold ${tone === 'danger' ? 'text-destructive' : ''}`}>{value}</p>
      {caption ? <p className="mt-1 text-xs text-muted-foreground">{caption}</p> : null}
    </CardContent>
  );

  return href ? (
    <Card className="transition-colors hover:border-primary/40">
      <Link to={href}>{body}</Link>
    </Card>
  ) : (
    <Card>{body}</Card>
  );
}

/** Time-of-day greeting, in the reader's own timezone. */
function greeting(name?: string): string {
  const hour = new Date().getHours();
  const part = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const first = name?.split(' ')[0];
  return first ? `${part}, ${first}` : part;
}
